import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatTab, MatTabGroup, PageEvent } from '@angular/material';
import { Meta, Title } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import * as debounce from 'lodash/debounce';
import { NEIGHBORSTATES } from '../../util/neighbor-states';
import { storageAvailable } from '../../util/helpers';

import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { ResultsMapComponent } from '../results-map/results-map.component';
import { SearchFormType } from '../models/search-form-type.model';
import { SearchQuery } from '../models/search-query.model';
import { State } from '../../shared/state-view/state.model';
import { QueryValidationReason } from '../models/query-validation-result.model';
import { Filter, FilterKeys } from '../filters/filters';
import { SearchFormComponent } from '../search-form/search-form.component';
import { AirportService, MAX_BBOX_AREA } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { PlaceResultWrapper } from '../../google/place-result-wrapper';
import { AirportResultsListingComponent } from '../airport-results-listing/airport-results-listing.component';
import { ActivityResultsListingComponent } from '../activity-results-listing/activity-results-listing.component';
import { EventResultsListingComponent } from '../event-results-listing/event-results-listing.component';
import { environment } from '../../../environments/environment';
import { BusinessFuel, FuelType } from '../../business/models/business-fuel.model';
import { AirportFuelSummary } from '../../airport/models/airport-fuel-summary.model';
import { GlobalSettingsService } from '../../shared/services/global-settings.service';
import { AviationEvent, AviationEventType } from '../../shared/models/aviation-event.model';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { AviationEventFiltersService } from '../../shared/services/aviation-event-filters.service';
import { AviationEventFilter } from '../../shared/models/aviation-event-filter.model';
import { FlyingClubResult } from '../../shared/models/flying-club-result.model';
import { FlyingClubsService } from '../../shared/services/flying-clubs.service';
import { FlyingClubFinderFiltersService } from '../../shared/services/flying-club-finder-filters.service';
import {
  FlyingClubFinderFilter,
  FLYING_CLUB_EQUITY_TAG,
  FLYING_CLUB_NON_EQUITY_TAG,
  FLYING_CLUB_HYBRID_EQUITY_TAG
} from '../../shared/models/flying-club-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { FlyingClubFinderResultsListingComponent } from '../flying-club-finder-results-listing/flying-club-finder-results-listing.component';
import { FlightSchoolResult } from '../../shared/models/flight-school-result.model';
import { FlightSchoolsService } from '../../shared/services/flight-schools.service';
import { FlightSchoolFinderFiltersService } from '../../shared/services/flight-school-finder-filters.service';
import { FlightSchoolFinderFilter } from '../../shared/models/flight-school-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { FlightSchoolFinderResultsListingComponent } from '../flight-school-finder-results-listing/flight-school-finder-results-listing.component';
import { UniversityResult } from '../../shared/models/university-result.model';
import { UniversitiesService } from '../../shared/services/universities.service';
import { UniversityFinderFiltersService } from '../../shared/services/university-finder-filters.service';
import { UniversityFinderFilter } from '../../shared/models/university-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { UniversityFinderResultsListingComponent } from '../university-finder-results-listing/university-finder-results-listing.component';
import { FboResult } from '../../shared/models/fbo-result.model';
import { FbosService } from '../../shared/services/fbos.service';
import { FboFinderFiltersService } from '../../shared/services/fbo-finder-filters.service';
import { FboFinderFilter } from '../../shared/models/fbo-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { FboFinderResultsListingComponent } from '../fbo-finder-results-listing/fbo-finder-results-listing.component';
import { ServiceRepairResult } from '../../shared/models/service-repair-result.model';
import { ServiceRepairsService } from '../../shared/services/service-repairs.service';
import { ServiceRepairFinderFiltersService } from '../../shared/services/service-repair-finder-filters.service';
import { ServiceRepairFinderFilter } from '../../shared/models/service-repair-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { ServiceRepairFinderResultsListingComponent } from '../service-repair-finder-results-listing/service-repair-finder-results-listing.component';
import { RentalSalesResult } from '../../shared/models/rental-sales-result.model';
import { RentalSalesService } from '../../shared/services/rental-sales.service';
import { RentalSalesFinderFiltersService } from '../../shared/services/rental-sales-finder-filters.service';
import { RentalSalesFinderFilter } from '../../shared/models/rental-sales-finder-filter.model';
// tslint:disable-next-line:max-line-length
import { RentalSalesFinderResultsListingComponent } from '../rental-sales-finder-results-listing/rental-sales-finder-results-listing.component';
import * as moment from 'moment-timezone';
import PlaceResult = google.maps.places.PlaceResult;
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';


/**
 * Period of time to wait (in milliseconds) before issuing a new bounding box search after panning or zooming
 * the map.
 */
const SEARCH_DEBOUNCE_TIME = 500;

/**
 * Max viewport bounding-box area (square degrees) for the University Finder before the "zoom in to update
 * results" message blocks a pan/zoom search — wide enough to cover the entire continental US in one view.
 * Universities are few enough nationwide (~300) that a user should be able to see them all without zooming in,
 * unlike the other map-searched finders which use the tighter {@link MAX_BBOX_AREA}.
 */
const UNIVERSITY_MAX_BBOX_AREA = 3000;


@Component({
  selector: 'app-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss']
})
export class SearchPageComponent implements OnInit, AfterViewInit, OnDestroy {
  query: SearchQuery = new SearchQuery();
  selectedTab: MatTab;
  sidebarOpened = true;
  airports: Array<AirportSearchResult> = [];
  activities: Array<PlaceResult> = [];
  aviationEvents: Array<AviationEvent> = [];
  activeAirport: AirportSearchResult;     // Airport being hovered (small tooltip shown on map, outline shown in list)
  selectedAirport: AirportSearchResult;   // Airport that was clicked (full size card opened on map)
  selectedActivity: PlaceResult;
  activeAviationEvent: AviationEvent;
  selectedAviationEvent: AviationEvent;
  airportState: State = State.Empty;
  activityState: State = State.Empty;
  aviationEventsState: State = State.Empty;
  errorMessage: string;
  pageIndex = 0;
  pageSize = 10;
  filteredAirports: Array<AirportSearchResult> = [];
  filteredAviationEvents: Array<AviationEvent> = [];
  declutteredAirports: Array<AirportSearchResult> = [];
  locationState: State = State.Empty;
  boundsChange$: Observable<BoundingBox>;
  prevBounds: BoundingBox;
  pauseBounds = false;
  mapMessage = null;
  _selectedAirportId = null;
  $sidebar = null;
  backToSearchVisible = false;
  _handler = null;
  fuelTypeSubscription: Subscription;
  fuelType: FuelType = FuelType._100LL;
  aviationEventFilterSubscription: Subscription;
  aviationEventFilter: AviationEventFilter;
  NEIGHBORSTATES = NEIGHBORSTATES;
  exceedsAirportBounds = false;
  eventPageIndex = 0;
  eventPageSize = 25;
  flyingClubs: Array<FlyingClubResult> = [];
  filteredFlyingClubs: Array<FlyingClubResult> = [];
  activeFlyingClub: FlyingClubResult;
  flyingClubsState: State = State.Empty;
  flyingClubPageIndex = 0;
  flyingClubPageSize = 25;
  flyingClubFinderFilter: FlyingClubFinderFilter;
  flyingClubFinderFilterSubscription: Subscription;
  flightSchools: Array<FlightSchoolResult> = [];
  filteredFlightSchools: Array<FlightSchoolResult> = [];
  activeFlightSchool: FlightSchoolResult;
  flightSchoolsState: State = State.Empty;
  flightSchoolPageIndex = 0;
  flightSchoolPageSize = 25;
  flightSchoolFinderFilter: FlightSchoolFinderFilter;
  flightSchoolFinderFilterSubscription: Subscription;
  universities: Array<UniversityResult> = [];
  filteredUniversities: Array<UniversityResult> = [];
  activeUniversity: UniversityResult;
  universitiesState: State = State.Empty;
  universityPageIndex = 0;
  universityPageSize = 25;
  universityFinderFilter: UniversityFinderFilter;
  universityFinderFilterSubscription: Subscription;
  fbos: Array<FboResult> = [];
  filteredFbos: Array<FboResult> = [];
  activeFbo: FboResult;
  fbosState: State = State.Empty;
  fboPageIndex = 0;
  fboPageSize = 25;
  fboFinderFilter: FboFinderFilter;
  fboFinderFilterSubscription: Subscription;

  serviceRepairs: Array<ServiceRepairResult> = [];
  filteredServiceRepairs: Array<ServiceRepairResult> = [];
  activeServiceRepair: ServiceRepairResult;
  serviceRepairsState: State = State.Empty;
  serviceRepairPageIndex = 0;
  serviceRepairPageSize = 25;
  serviceRepairFinderFilter: ServiceRepairFinderFilter;
  serviceRepairFinderFilterSubscription: Subscription;

  rentalSales: Array<RentalSalesResult> = [];
  filteredRentalSales: Array<RentalSalesResult> = [];
  activeRentalSales: RentalSalesResult;
  rentalSalesState: State = State.Empty;
  rentalSalesPageIndex = 0;
  rentalSalesPageSize = 25;
  rentalSalesFinderFilter: RentalSalesFinderFilter;
  rentalSalesFinderFilterSubscription: Subscription;


  @ViewChild('form') form: SearchFormComponent;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  @ViewChild('airportsTab') airportsTab: MatTab;
  @ViewChild('activitiesTab') activitiesTab: MatTab;
  @ViewChild('aviationEventsTab') aviationEventsTab: MatTab;
  @ViewChild('flyingClubsTab') flyingClubsTab: MatTab;
  @ViewChild('flightSchoolsTab') flightSchoolsTab: MatTab;
  @ViewChild('universitiesTab') universitiesTab: MatTab;
  @ViewChild('fbosTab') fbosTab: MatTab;
  @ViewChild('serviceRepairsTab') serviceRepairsTab: MatTab;
  @ViewChild('rentalSalesTab') rentalSalesTab: MatTab;
  @ViewChild('airportResultsListing') airportResultsListing: AirportResultsListingComponent;
  @ViewChild('activityResultsListing') activityResultsListing: ActivityResultsListingComponent;
  @ViewChild('eventsResultsListing') eventsResultsListing: EventResultsListingComponent;
  @ViewChild('flyingClubFinderResultsListing') flyingClubFinderResultsListing: FlyingClubFinderResultsListingComponent;
  // tslint:disable-next-line:max-line-length
  @ViewChild('flightSchoolFinderResultsListing') flightSchoolFinderResultsListing: FlightSchoolFinderResultsListingComponent;
  // tslint:disable-next-line:max-line-length
  @ViewChild('universityFinderResultsListing') universityFinderResultsListing: UniversityFinderResultsListingComponent;
  // tslint:disable-next-line:max-line-length
  @ViewChild('fboFinderResultsListing') fboFinderResultsListing: FboFinderResultsListingComponent;
  @ViewChild('serviceRepairFinderResultsListing') serviceRepairFinderResultsListing: ServiceRepairFinderResultsListingComponent;
  // tslint:disable-next-line:max-line-length
  @ViewChild('rentalSalesFinderResultsListing') rentalSalesFinderResultsListing: RentalSalesFinderResultsListingComponent;
  @ViewChild('map') map: ResultsMapComponent;
  @ViewChild('backToSearchEl') backToSearchEl: ElementRef;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _meta: Meta,
    private _title: Title,
    private _airportService: AirportService,
    private _google: GoogleService,
    private _cdref: ChangeDetectorRef,
    private _errorHandler: ErrorHandler,
    private _globalSettingsService: GlobalSettingsService,
    private _aviationEventsService: AviationEventsService,
    private _aviationEventFiltersService: AviationEventFiltersService,
    private _flyingClubsService: FlyingClubsService,
    private _flyingClubFinderFiltersService: FlyingClubFinderFiltersService,
    private _flightSchoolsService: FlightSchoolsService,
    private _flightSchoolFinderFiltersService: FlightSchoolFinderFiltersService,
    private _universitiesService: UniversitiesService,
    private _universityFinderFiltersService: UniversityFinderFiltersService,
    private _fbosService: FbosService,
    private _fboFinderFiltersService: FboFinderFiltersService,
    private _serviceRepairsService: ServiceRepairsService,
    private _serviceRepairFinderFiltersService: ServiceRepairFinderFiltersService,
    private _rentalSalesService: RentalSalesService,
    private _rentalSalesFinderFiltersService: RentalSalesFinderFiltersService,
    public _snackBar: MatSnackBar,
    private _analyticsService: AnalyticsService,
    private _auth: AuthService
  ) {
    this._title.setTitle(`Airport Search - ${environment.appName}`);
    this._meta.removeTag('name="description"');
  }

  get searchType(): SearchFormType {
    const tab = this._getSelectedTab();
    if (tab === this.activitiesTab) {
      return SearchFormType.Activities;
    } else if (tab === this.aviationEventsTab) {
      return SearchFormType.AviationEvents;
    } else if (tab === this.flyingClubsTab) {
      return SearchFormType.FlyingClubs;
    } else if (tab === this.flightSchoolsTab) {
      return SearchFormType.FlightSchools;
    } else if (tab === this.universitiesTab) {
      return SearchFormType.Universities;
    } else if (tab === this.fbosTab) {
      return SearchFormType.Fbos;
    } else if (tab === this.serviceRepairsTab) {
      return SearchFormType.ServiceRepair;
    } else if (tab === this.rentalSalesTab) {
      return SearchFormType.RentalSales;
    } else {
      if (this.query && this.query.type && this.query.type === SearchFormType.FuelPrice) {
        return SearchFormType.FuelPrice;
      } else {
        return SearchFormType.Airports;
      }
    }
  }

  get toggleTooltip(): string {
    return this.sidebarOpened ? 'Collapse sidebar' : 'Expand sidebar';
  }

  ngOnInit() {
    this._initBoundsListener();
    this.fuelTypeSubscription = this._globalSettingsService.fuelType.subscribe(fuel => {
      this.fuelType = BusinessFuel.parseFuelType(fuel);
    });
    this.aviationEventFilterSubscription = this._aviationEventFiltersService.aviationEventFilter.subscribe(ae => {
      this.aviationEventFilter = ae;
    });
    this.flyingClubFinderFilterSubscription = this._flyingClubFinderFiltersService.flyingClubFinderFilter.subscribe(f => {
      this.flyingClubFinderFilter = f;
    });
    this.flightSchoolFinderFilterSubscription = this._flightSchoolFinderFiltersService.flightSchoolFinderFilter.subscribe(f => {
      this.flightSchoolFinderFilter = f;
    });
    this.universityFinderFilterSubscription = this._universityFinderFiltersService.universityFinderFilter.subscribe(f => {
      this.universityFinderFilter = f;
    });
    this.fboFinderFilterSubscription = this._fboFinderFiltersService.fboFinderFilter.subscribe(f => {
      this.fboFinderFilter = f;
    });
    this.serviceRepairFinderFilterSubscription = this._serviceRepairFinderFiltersService.serviceRepairFinderFilter.subscribe(f => {
      this.serviceRepairFinderFilter = f;
    });
    this.rentalSalesFinderFilterSubscription = this._rentalSalesFinderFiltersService.rentalSalesFinderFilter.subscribe(f => {
      this.rentalSalesFinderFilter = f;
    });
    this._analyticsService.pageView('airport_search', 'Airport/Event Search');
    if (this._auth.authenticated && this._auth.endUser) {
      this._analyticsService.trackEvent('airport_search_loggedin', this._auth.recid, this._auth.recid);
    } else {
      this._analyticsService.trackEvent('airport_search_loggedout', '', '');
    }
  }

  /**
   * True only during the initial view setup, while the search type's tab is being selected programmatically.
   * Setting tabGroup.selectedIndex fires (selectedTabChange) → onSelectTab, which would run the finder search
   * a second time (the setTimeout in ngAfterViewInit is the canonical initial search). This flag makes
   * onSelectTab skip that redundant initial search so the finder endpoint isn't hit twice on page load.
   */
  private _suppressInitialTabSearch = false;

  /**
   * The state/province (e.g. "CA") of the current state search, preserved so switching to a different finder
   * tab can re-run the search for that state. Each per-tab finder search nulls this.query.state after using it
   * (see the state branches in searchFbos / searchFlyingClubs / ...), so without this the state would be lost
   * the moment you switch finders and the new tab would have no query — showing an empty map and the
   * "Please enter a search query" placeholder. Captured whenever a fresh query is established (deserializeQuery
   * on load, search() on form submit); null for any non-state search so it never hijacks a name/location search.
   */
  private _finderStateProvince: string = null;

  ngAfterViewInit() {
    // Set the initial tab based on search type. Suppress onSelectTab's search while we do this — the
    // setTimeout below performs the canonical initial search.
    this._suppressInitialTabSearch = true;
    const query = SearchQuery.decode(this._route.snapshot.queryParamMap);
    if (query.type === SearchFormType.Activities) {
      this.tabGroup.selectedIndex = 1;
    }
    if (query.type === SearchFormType.AviationEvents) {
      this.tabGroup.selectedIndex = 2;
      this._aviationEventFiltersService.updateAviationEventFilters(query.aviationEventsFilter);
    }
    if (query.type === SearchFormType.FlyingClubs) {
      this.tabGroup.selectedIndex = 3;
      this._flyingClubFinderFiltersService.updateFlyingClubFinderFilters(query.flyingClubFinderFilter);
    }
    if (query.type === SearchFormType.FlightSchools) {
      this.tabGroup.selectedIndex = 4;
      this._flightSchoolFinderFiltersService.updateFlightSchoolFinderFilters(query.flightSchoolFinderFilter);
    }
    if (query.type === SearchFormType.Universities) {
      this.tabGroup.selectedIndex = 5;
      this._universityFinderFiltersService.updateUniversityFinderFilters(query.universityFinderFilter);
    }
    if (query.type === SearchFormType.Fbos) {
      this.tabGroup.selectedIndex = 6;
      this._fboFinderFiltersService.updateFboFinderFilters(query.fboFinderFilter);
    }
    if (query.type === SearchFormType.ServiceRepair) {
      this.tabGroup.selectedIndex = 7;
      this._serviceRepairFinderFiltersService.updateServiceRepairFinderFilters(query.serviceRepairFinderFilter);
    }
    if (query.type === SearchFormType.RentalSales) {
      this.tabGroup.selectedIndex = 8;
      this._rentalSalesFinderFiltersService.updateRentalSalesFinderFilters(query.rentalSalesFinderFilter);
    }

    // Wrapped in setTimeout to avoid triggering change detection error
    // ("Expression has changed after it was checked").
    setTimeout(() => {
      // Store the initially selected tab.
      this.selectedTab = this._getSelectedTab();

      // Deserialize the search query from the URL
      this.deserializeQuery();

      // Perform the initial search, but only if the selected tab is the default one. Otherwise,
     // rely on the onSelectTab event handler to perform the initial search.
       this.searchAirports(false);
     if (this.tabGroup.selectedIndex === 1) {
       this.searchActivities();
      }
      if (this.tabGroup.selectedIndex === 2) {
        this.searchEvents(false);
      }
      if (this.tabGroup.selectedIndex === 3) {
        this.searchFlyingClubs();
      }
      if (this.tabGroup.selectedIndex === 4) {
        this.searchFlightSchools();
      }
      if (this.tabGroup.selectedIndex === 5) {
        this.searchUniversities();
      }
      if (this.tabGroup.selectedIndex === 6) {
        this.searchFbos();
      }
      if (this.tabGroup.selectedIndex === 7) {
        this.searchServiceRepairs();
      }
      if (this.tabGroup.selectedIndex === 8) {
        this.searchRentalSales();
      }
      // Initial searches done; re-enable onSelectTab's search for subsequent user-driven tab changes.
      this._suppressInitialTabSearch = false;
    }, 0);

    // Initialize page resize/scroll listener, which updates the visibility of a "Back to Search" button that
    // allows getting back to the search form. This is primarily useful on mobile, where a user might zoom in
    // and scroll the page into the map - at which point, scrolling back is not possible since swiping results
    // in panning the map instead of scrolling.
    // @ts-ignore
    this.$sidebar = $('.search-page__sidebar');
    this._handler = debounce(this.updateBackToSearch.bind(this), 300);
    setTimeout(() => this.updateBackToSearch(), 300);
    // @ts-ignore
    $(window).on('resize scroll', this._handler);
  }

  ngOnDestroy() {
    if (this._handler) {
      // @ts-ignore
      $(window).off('resize scroll', this._handler);
    }
    this.aviationEventFilterSubscription.unsubscribe();
    if (this.flyingClubFinderFilterSubscription) {
      this.flyingClubFinderFilterSubscription.unsubscribe();
    }
    if (this.flightSchoolFinderFilterSubscription) {
      this.flightSchoolFinderFilterSubscription.unsubscribe();
    }
    if (this.universityFinderFilterSubscription) {
      this.universityFinderFilterSubscription.unsubscribe();
    }
    if (this.fboFinderFilterSubscription) {
      this.fboFinderFilterSubscription.unsubscribe();
    }
    if (this.serviceRepairFinderFilterSubscription) {
      this.serviceRepairFinderFilterSubscription.unsubscribe();
    }
    if (this.rentalSalesFinderFilterSubscription) {
      this.rentalSalesFinderFilterSubscription.unsubscribe();
    }
  }

  onSelectTab(event) {
    this.mapMessage = null;
    this.selectedTab = event.tab;
    // During initial view setup the tab is selected programmatically; the ngAfterViewInit setTimeout performs
    // the initial search, so skip here to avoid firing the (possibly slow) finder search twice on page load.
    if (this._suppressInitialTabSearch) {
      return;
    }
    this.serializeQuery();
      if (this.selectedTab === this.airportsTab) {
        this.activities = [];
        this.aviationEvents = [];
        this.filteredAviationEvents = [];
        this.searchAirports();
        this.onFiltersChanged();
      }
    // Reload airports (if not already loaded), regardless of which tab is open.
      if (this.airportState === State.Empty || this.airportState === State.Error) {
          this.searchAirports();
          this.onFiltersChanged();
      }
    // Reload activities (if not already loaded), but only if the activities tab is open.
     if (this.selectedTab === this.activitiesTab) {
       this.aviationEvents = [];
       this.filteredAviationEvents = [];
       this.searchActivities();
       this.onFiltersChanged();
     }
    // Reload aviationEvents (if not already loaded), but only if the events tab is open.
    if (this.selectedTab === this.aviationEventsTab) {
      this.activities = [];
      this.searchEvents();
      this.onFiltersChanged();
    }
    // Reload flying clubs, but only if the flying clubs tab is open.
    if (this.selectedTab === this.flyingClubsTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchFlyingClubs();
    }
    // Reload flight schools, but only if the flight schools tab is open.
    if (this.selectedTab === this.flightSchoolsTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchFlightSchools();
    }
    // Reload universities, but only if the universities tab is open.
    if (this.selectedTab === this.universitiesTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchUniversities();
    }
    // Reload FBOs, but only if the businesses tab is open.
    if (this.selectedTab === this.fbosTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchFbos();
    }
    // Reload service/repair businesses, but only if the service/repair tab is open.
    if (this.selectedTab === this.serviceRepairsTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchServiceRepairs();
    }
    // Reload rentals/sales businesses, but only if the rentals/sales tab is open.
    if (this.selectedTab === this.rentalSalesTab) {
      this.activities = [];
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.searchRentalSales();
    }
  }

  private _getSelectedTab(): MatTab {
    if (!this.tabGroup._tabs) {
      return null;
    }
    return this.tabGroup._tabs
      .toArray()
      .find((tab: MatTab, i: number) => i === this.tabGroup.selectedIndex);
  }

  /**
   * Returns the minimum difference in the latitude or longitude of either corner of the viewport before considering the
   * new viewport to be different from the previous and issuing a new search. This is based on the previous viewport,
   * and is dependent on the scale (a difference of 0.1 degrees may be negligible when zoomed out, but significant
   * when zoomed in, so we cannot use a constant value).
   * @param bounds {BoundingBox} Viewport bounding box to use as the basis for calculating the epsilon value
   * @returns {number} Epsilon value
   */
  getBoundingBoxEpsilon(bounds: BoundingBox): number {
    return 0.05 * Math.min(
      Math.abs(bounds.ne.lat - bounds.sw.lat),
      Math.abs(bounds.ne.lon - bounds.sw.lon)
    );
  }

  get pagedAirports(): Array<AirportSearchResult> {
    if (!this.filteredAirports) {
      return [];
    }
    const start = this.pageIndex * this.pageSize;
    return this.filteredAirports.slice(start, start + this.pageSize);
  }

  get pagedAviationEvents(): Array<AviationEvent> {
    if (!this.filteredAviationEvents) {
      return [];
    }
    const start = this.eventPageIndex * this.eventPageSize;
    return this.filteredAviationEvents.slice(start, start + this.eventPageSize);
  }

  get pagedFlyingClubs(): Array<FlyingClubResult> {
    if (!this.filteredFlyingClubs) {
      return [];
    }
    const start = this.flyingClubPageIndex * this.flyingClubPageSize;
    return this.filteredFlyingClubs.slice(start, start + this.flyingClubPageSize);
  }

  get pagedFlightSchools(): Array<FlightSchoolResult> {
    if (!this.filteredFlightSchools) {
      return [];
    }
    const start = this.flightSchoolPageIndex * this.flightSchoolPageSize;
    return this.filteredFlightSchools.slice(start, start + this.flightSchoolPageSize);
  }

  get pagedUniversities(): Array<UniversityResult> {
    if (!this.filteredUniversities) {
      return [];
    }
    const start = this.universityPageIndex * this.universityPageSize;
    return this.filteredUniversities.slice(start, start + this.universityPageSize);
  }

  get pagedFbos(): Array<FboResult> {
    if (!this.filteredFbos) {
      return [];
    }
    const start = this.fboPageIndex * this.fboPageSize;
    return this.filteredFbos.slice(start, start + this.fboPageSize);
  }

  get pagedServiceRepairs(): Array<ServiceRepairResult> {
    if (!this.filteredServiceRepairs) {
      return [];
    }
    const start = this.serviceRepairPageIndex * this.serviceRepairPageSize;
    return this.filteredServiceRepairs.slice(start, start + this.serviceRepairPageSize);
  }

  get pagedRentalSales(): Array<RentalSalesResult> {
    if (!this.filteredRentalSales) {
      return [];
    }
    const start = this.rentalSalesPageIndex * this.rentalSalesPageSize;
    return this.filteredRentalSales.slice(start, start + this.rentalSalesPageSize);
  }

  get includePrivate(): boolean {
    if (!this.query) {
      return false;
    }
    const f = <Filter>this.query.getFilter(FilterKeys.PublicUse);
    return !f.enabled;
  }

  search(keepPriceSliderState?: boolean) {
    // Preserve the state search for finder tab-switching (see _finderStateProvince). Reflects the current
    // query's state, or null for a non-state search so a stale state can't hijack a later name/location search.
    this._finderStateProvince = this.query.state || null;
    /** This forces all state queries to search by airport and switches back to the airport tab */
    if (this.query.state && (this._getSelectedTab() === this.activitiesTab || this._getSelectedTab() === this.aviationEventsTab)) {
      this.tabGroup.selectedIndex = 0;
    }
    if (!this.exceedsAirportBounds && this.selectedTab !== this.flyingClubsTab && this.selectedTab !== this.flightSchoolsTab &&
        this.selectedTab !== this.universitiesTab && this.selectedTab !== this.fbosTab && this.selectedTab !== this.serviceRepairsTab &&
        this.selectedTab !== this.rentalSalesTab &&
        (this.selectedTab !== this.aviationEventsTab || this.eventsResultsListing.showAirports)) {
      this.searchAirports(keepPriceSliderState);
    }
    if (this.query.type === SearchFormType.Activities) {
      this.searchActivities();
    }
    if (this.query.type === SearchFormType.AviationEvents) {
      this.searchEvents(keepPriceSliderState);
    }
    if (this.query.type === SearchFormType.FlyingClubs) {
      this.searchFlyingClubs();
    }
    if (this.query.type === SearchFormType.FlightSchools) {
      this.searchFlightSchools();
    }
    if (this.query.type === SearchFormType.Universities) {
      this.searchUniversities();
    }
    if (this.query.type === SearchFormType.Fbos) {
      this.searchFbos();
    }
    if (this.query.type === SearchFormType.ServiceRepair) {
      this.searchServiceRepairs();
    }
    if (this.query.type === SearchFormType.RentalSales) {
      this.searchRentalSales();
    }
  }

  async searchAirports(keepPriceSliderState?: boolean) {
    try {
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.airportState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.term) {
          this.airports = await this._airportService.getAirportsBySearchTerm(this.query.term, this.includePrivate);
        } else if (this.query.location && this.query.radius) {
          this.airports = await this._airportService.getAirportsByLatLon(this.query.location, this.query.radius, this.includePrivate);
        } else if (this.query.bounds) {
          try {
            this.airports = await this._airportService.getAirportsByBoundingBox(this.query.bounds, this.includePrivate);
          } catch (e) {
            if (e.message.includes('Bounding box area')) {
              this.mapMessage = 'Zoom in to update results';
            } else {
              throw e;
            }
          }
        } else if (this.query.state) {
          this.airports = await this._airportService.getAirportsByState(this.query.state, this.includePrivate);
          this.activities = [];
          this.query.state = null;
        } else {
          // Events and the business finders (Flying Clubs / Flight Schools / Universities / FBOs) can be
          // searched with no airport-usable base param — e.g. by name alone — which makes query.validate()
          // pass even though there is nothing to search airports by. In that case there simply are no
          // airports to show (rather than an error). Airport-type searches still require a base param.
          if (this.query.type === SearchFormType.AviationEvents
              || this.query.type === SearchFormType.FlyingClubs
              || this.query.type === SearchFormType.FlightSchools
              || this.query.type === SearchFormType.Universities
              || this.query.type === SearchFormType.Fbos
              || this.query.type === SearchFormType.ServiceRepair
              || this.query.type === SearchFormType.RentalSales) {
            this.airports = [];
          } else {
            throw new Error('Either a search term, a location and radius, or a bounding box is required to search airports.');
          }
        }
        this.filteredAirports = this.query.execute(this.airports);
        this._restoreSelectedAirport();
        this.airportState = State.Content;
        this.form.enableForm();
        if (!this.query.bounds) {
          this._updateBounds();
        }
        this.onFiltersChanged(keepPriceSliderState);
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.airportState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.airportState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.airports = [];
      this.filteredAirports = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.airportState = State.Error;
    }
  }

  async searchActivities() {
    try {
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        if (!this.query.activity) {
          this.activityState = State.Empty;
          return;
        }
        this.activityState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        let result: PlaceResultWrapper;
        if (this.query.location && this.query.radius) {
          result = await this._google.searchActivitiesByLatLon(this.query.activity, this.query.location, this.query.radius);
        } else if (this.query.bounds) {
          result = await this._google.searchActivitiesByBoundingBox(this.query.activity, this.query.bounds);
        } else {
          throw new Error('Either a location and radius or a bounding box is required to search activities');
        }
        this.activities = result.places;
        this.activityState = State.Content;
        this.form.enableForm();
        if (!this.query.bounds) {
          this._updateBounds();
        }
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.activityState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.activityState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.activities = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.activityState = State.Error;
    }
  }

  onAviationEventsFiltersChanged() {
    this.serializeQuery();
    this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
  }

  onAviationEventsFiltersDateChanged() {
    this.serializeQuery();
    this.searchEvents();
    // this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
  }

  filterAviationEvents(aviationEvents: AviationEvent[]): AviationEvent[] {
    let filteredEvents: Array<AviationEvent> = [];
    aviationEvents.forEach((aviationEvent) => {
      if (aviationEvent.type === AviationEventType.Airshow && this.query.aviationEventsFilter.airshow === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.AirshowwithMilitaryDemonstrationTeam &&
        this.query.aviationEventsFilter.airshowwithmilitarydemonstrationteam === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.FlyIn && this.query.aviationEventsFilter.flyin === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.FlyOut && this.query.aviationEventsFilter.flyout === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.PancakeBreakfast && this.query.aviationEventsFilter.pancakeBreakfast === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.SocialGathering && this.query.aviationEventsFilter.socialGathering === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.Openhouse && this.query.aviationEventsFilter.openhouse === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.PokerRun && this.query.aviationEventsFilter.pokerRun === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.AviationCommunityDay && this.query.aviationEventsFilter.aviationCommunityDay === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.WingsAndWheelsEvent && this.query.aviationEventsFilter.wingsAndWheels === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.AircraftDisplayRides && this.query.aviationEventsFilter.aircraftDisplayRides === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.RustyPilotsSeminar && this.query.aviationEventsFilter.aopaRustyPilots === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.FlyingClubSeminar && this.query.aviationEventsFilter.aopaFlyingClub === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.SafetySeminar && this.query.aviationEventsFilter.safetySeminar === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.Conference && this.query.aviationEventsFilter.conference === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.Workshop && this.query.aviationEventsFilter.workshop === true) {
        filteredEvents.push(aviationEvent);
      }
      if (aviationEvent.type === AviationEventType.BackToYourRoots && this.query.aviationEventsFilter.aopaBacktoYourRoots === true) {
        filteredEvents.push(aviationEvent);
      }
    });

    const queryStart = moment(new Date(this.query.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
    const queryEnd = moment(new Date(this.query.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));

    filteredEvents = filteredEvents.filter((ae) => {
      const eventStart = moment(new Date(ae.startDateTime), 'UTC').tz(ae.timezone);
      const eventEnd = moment(new Date(ae.endDateTime), 'UTC').tz(ae.timezone);
      if ((eventStart >= queryStart && eventStart <= queryEnd) || (eventEnd >= queryStart && eventEnd <= queryEnd)) {
        return true;
      } else {
        return false;
      }
    });

    filteredEvents = filteredEvents.filter((ae) =>
      ae.title.toLowerCase().includes(this.query.aviationEventsFilter.name.toLowerCase()));

    filteredEvents = filteredEvents.filter((ae) => {
      if (this.query.aviationEventsFilter.formatOnline && (ae.format === 'Online' || ae.format === 'Both')) {
        return true;
      } else if (this.query.aviationEventsFilter.formatInPerson && (ae.format === 'In-Person' || ae.format === 'Both') &&
        (ae.location && ae.location.lat && ae.location.lon)) {
        return true;
      } else {
        return false;
      }
    });

    // check to see if there are any visible in-person events
    const visibleInPersonEvents = filteredEvents.some(ae => {
      if ((ae.format === 'Both' || ae.format === 'In-Person') && ae.location && ae.location.lat && ae.location.lon) {
        return true;
      }
      return false;
    });
    if (visibleInPersonEvents) {
      if (this.mapMessage && this.mapMessage.startsWith('Zoom in to update ')) {
      } else {
        this.mapMessage = null;
      }
    } else {
      if (this.query.aviationEventsFilter.formatOnline && !this.query.aviationEventsFilter.formatInPerson) {
        this.mapMessage = 'You have filtered out In-Person events';
      } else if (this.query.aviationEventsFilter.formatInPerson) {
        this.mapMessage = 'No In-Person events to show in this area';
      }
    }

    return filteredEvents;
  }

  async searchEvents(keepPriceSliderState?: boolean) {
    try {
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.aviationEventsState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        /* if (this.query.term) {
          this.aviationEvents = await this._aviationEventsService.getAviationEventsBySearchTerm(this.query.term);
        } else */
        const queryStartDate = moment(new Date(this.query.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
        const queryStartDateUTC = queryStartDate.clone().tz('UTC').format();
        const queryEndDate = moment(new Date(this.query.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));
        const queryEndDateUTC = queryEndDate.clone().tz('UTC').format();
        if (this.query.location && this.query.radius) {
          this.aviationEvents = await this._aviationEventsService.getAviationEventsByLatLon(this.query.location, this.query.radius,
            queryStartDateUTC, queryEndDateUTC);
          if (!this.query.bounds) {
            this._updateBounds();
          }
          /* if (this.prevBounds) {
            this.onBoundsChanged(this.prevBounds);
          } */
          // this.aviationEvents = await this._aviationEventsService.getAviationEventsByLatLon(this.query.location, this.query.radius);
        } else if (this.query.bounds) {
          this.aviationEvents = await this._aviationEventsService.getAviationEventsByBoundingBox(this.query.bounds,
            queryStartDateUTC, queryEndDateUTC);
        } else if (this.query.state) {
          this.aviationEvents = await this._aviationEventsService.getAviationEventsByState(this.query.state,
            queryStartDateUTC, queryEndDateUTC);
          this.activities = [];
          this.query.state = null;
        } else {
          this.aviationEvents = await this._aviationEventsService.getAviationEvents(queryStartDateUTC, queryEndDateUTC);
        }
        this.filteredAviationEvents = this.filterAviationEvents(this.aviationEvents);
        this.aviationEventsState = State.Content;
        this.form.enableForm();
        /* if (!this.query.bounds) {
          this._updateBounds();
        } */
        this.onFiltersChanged(keepPriceSliderState);
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.aviationEventsState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.aviationEventsState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.aviationEvents = [];
      this.filteredAviationEvents = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.aviationEventsState = State.Error;
    }
  }

  onFlyingClubFiltersChanged() {
    this.serializeQuery();
    this.filteredFlyingClubs = this.filterFlyingClubs(this.flyingClubs);
  }

  /**
   * Applies the category facets client-side, mirroring the iOS Business filter logic: club-type and
   * Membership use OR logic; Benefits and Types of Flying use AND logic; the keyword matches name or
   * airport ident. (Location/state/keyword narrowing already happened server-side.)
   */
  filterFlyingClubs(flyingClubs: FlyingClubResult[]): FlyingClubResult[] {
    const f = this.query.flyingClubFinderFilter;
    if (!f) {
      return flyingClubs;
    }
    return flyingClubs.filter(club => {
      const tags = club.tags || [];

      // Club type (AOPA Network / In Formation) — OR logic within the group.
      const clubTypeFilters: string[] = [];
      if (f.aopaNetwork) {
        clubTypeFilters.push('AOPA Network Club');
      }
      if (f.inFormation) {
        clubTypeFilters.push('Club In Formation');
      }
      if (clubTypeFilters.length && clubTypeFilters.indexOf(club.clubType) === -1) {
        return false;
      }

      // Equity / Non-Equity club — OR logic; Hybrid-Equity clubs match whenever either is selected.
      const equityFilters: string[] = [];
      if (f.equityClub) {
        equityFilters.push(FLYING_CLUB_EQUITY_TAG);
      }
      if (f.nonEquityClub) {
        equityFilters.push(FLYING_CLUB_NON_EQUITY_TAG);
      }
      if (equityFilters.length && !equityFilters.some(t => tags.indexOf(t) !== -1)
        && tags.indexOf(FLYING_CLUB_HYBRID_EQUITY_TAG) === -1) {
        return false;
      }

      // Membership — OR logic (any selected tag).
      if (f.membership.length && !f.membership.some(m => tags.indexOf(m) !== -1)) {
        return false;
      }

      // Benefits — AND logic (all selected tags).
      if (f.benefits.length && !f.benefits.every(b => tags.indexOf(b) !== -1)) {
        return false;
      }

      // Types of Flying — AND logic.
      if (f.flying.length && !f.flying.every(fl => tags.indexOf(fl) !== -1)) {
        return false;
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (club.name || '').toLowerCase();
        const ident = (club.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchFlyingClubs() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.flyingClubsState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.flyingClubs = await this._flyingClubsService.getFlyingClubsByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.flyingClubs = await this._flyingClubsService.getFlyingClubsByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.flyingClubs = await this._flyingClubsService.getFlyingClubsByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned clubs so the
          // results (e.g. clubs near a ZIP) are framed rather than leaving the map zoomed out to CONUS.
          this.flyingClubs = await this._flyingClubsService.getFlyingClubsBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.flyingClubFinderFilter && this.query.flyingClubFinderFilter.name) {
          // No location was entered, but a club name was: search by name (server-side name/ident match).
          this.flyingClubs = await this._flyingClubsService.getFlyingClubsBySearchTerm(this.query.flyingClubFinderFilter.name);
          this._updateBounds();
        } else {
          this.flyingClubs = [];
        }
        this.filteredFlyingClubs = this.filterFlyingClubs(this.flyingClubs);
        this.flyingClubsState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.flyingClubsState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.flyingClubsState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.flyingClubs = [];
      this.filteredFlyingClubs = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.flyingClubsState = State.Error;
    }
  }

  onFlightSchoolFiltersChanged() {
    this.serializeQuery();
    this.filteredFlightSchools = this.filterFlightSchools(this.flightSchools);
  }

  /**
   * Applies the category facets client-side, mirroring the iOS Business filter logic: Training Type uses OR
   * logic (Part 61 OR Part 141); Certifications / Ratings / Advanced / Services / Additional use AND logic;
   * the keyword matches name or airport ident. (Location/state/keyword narrowing already happened
   * server-side.)
   */
  filterFlightSchools(flightSchools: FlightSchoolResult[]): FlightSchoolResult[] {
    const f = this.query.flightSchoolFinderFilter;
    if (!f) {
      return flightSchools;
    }
    return flightSchools.filter(school => {
      const tags = school.tags || [];

      // Training Type — OR logic (any selected tag).
      if (f.classification.length && !f.classification.some(c => tags.indexOf(c) !== -1)) {
        return false;
      }

      // Certifications — AND logic (all selected tags).
      if (f.certifications.length && !f.certifications.every(c => tags.indexOf(c) !== -1)) {
        return false;
      }

      // Ratings — AND logic.
      if (f.ratings.length && !f.ratings.every(r => tags.indexOf(r) !== -1)) {
        return false;
      }

      // Advanced Training — AND logic.
      if (f.advanced.length && !f.advanced.every(a => tags.indexOf(a) !== -1)) {
        return false;
      }

      // Services — AND logic.
      if (f.services.length && !f.services.every(s => tags.indexOf(s) !== -1)) {
        return false;
      }

      // Additional (VA Approved / College Affiliated) — AND logic.
      if (f.other.length && !f.other.every(o => tags.indexOf(o) !== -1)) {
        return false;
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (school.name || '').toLowerCase();
        const ident = (school.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchFlightSchools() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.flightSchoolsState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.flightSchools = await this._flightSchoolsService.getFlightSchoolsByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.flightSchools = await this._flightSchoolsService.getFlightSchoolsByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.flightSchools = await this._flightSchoolsService.getFlightSchoolsByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned schools so the
          // results (e.g. schools near a ZIP) are framed rather than leaving the map zoomed out to CONUS.
          this.flightSchools = await this._flightSchoolsService.getFlightSchoolsBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.flightSchoolFinderFilter && this.query.flightSchoolFinderFilter.name) {
          // No location was entered, but a school name was: search by name (server-side name/ident match).
          this.flightSchools = await this._flightSchoolsService.getFlightSchoolsBySearchTerm(this.query.flightSchoolFinderFilter.name);
          this._updateBounds();
        } else {
          this.flightSchools = [];
        }
        this.filteredFlightSchools = this.filterFlightSchools(this.flightSchools);
        this.flightSchoolsState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.flightSchoolsState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.flightSchoolsState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.flightSchools = [];
      this.filteredFlightSchools = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.flightSchoolsState = State.Error;
    }
  }

  onUniversityFiltersChanged() {
    this.serializeQuery();
    this.filteredUniversities = this.filterUniversities(this.universities);
  }

  /**
   * Applies the category facets client-side, mirroring the iOS Business filter logic for colleges: Degrees /
   * Majors / Accreditation / Additional all use AND logic (empty `orLogicCategories`); the keyword matches
   * name or airport ident. (Location/state/keyword narrowing already happened server-side.)
   */
  filterUniversities(universities: UniversityResult[]): UniversityResult[] {
    const f = this.query.universityFinderFilter;
    if (!f) {
      return universities;
    }
    return universities.filter(university => {
      const tags = university.tags || [];

      // Degrees — AND logic (all selected tags).
      if (f.degrees.length && !f.degrees.every(d => tags.indexOf(d) !== -1)) {
        return false;
      }

      // Majors — AND logic.
      if (f.majors.length && !f.majors.every(m => tags.indexOf(m) !== -1)) {
        return false;
      }

      // Accreditation — AND logic.
      if (f.accreditation.length && !f.accreditation.every(a => tags.indexOf(a) !== -1)) {
        return false;
      }

      // Additional (VA Approved / Distance Learning) — AND logic.
      if (f.options.length && !f.options.every(o => tags.indexOf(o) !== -1)) {
        return false;
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (university.name || '').toLowerCase();
        const ident = (university.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchUniversities() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.universitiesState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.universities = await this._universitiesService.getUniversitiesByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.universities = await this._universitiesService.getUniversitiesByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.universities = await this._universitiesService.getUniversitiesByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned universities so
          // the results (e.g. universities near a ZIP) are framed rather than leaving the map zoomed out.
          this.universities = await this._universitiesService.getUniversitiesBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.universityFinderFilter
            && AirportService.sanitizeSearchTerm(this.query.universityFinderFilter.name).length >= 3) {
          // No location was entered, but a university name was: search by name (server-side name/ident match).
          this.universities = await this._universitiesService.getUniversitiesBySearchTerm(this.query.universityFinderFilter.name);
          this._updateBounds();
        } else {
          // No location, state, or keyword was entered. Universities are few enough nationwide (~300) that
          // an unscoped search returns every university rather than requiring a location.
          this.universities = await this._universitiesService.getAllUniversities();
          this._updateBounds();
        }
        this.filteredUniversities = this.filterUniversities(this.universities);
        this.universitiesState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.universitiesState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.universitiesState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.universities = [];
      this.filteredUniversities = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.universitiesState = State.Error;
    }
  }

  onFboFiltersChanged() {
    this.serializeQuery();
    this.filteredFbos = this.filterFbos(this.fbos);
  }

  /**
   * Applies the category facets client-side, mirroring the iOS FBO filter logic: Fuel / Fuel Type / Rental
   * Car / Transportation use OR logic; Amenities / Facilities / Line Services use AND logic; the keyword
   * matches name or airport ident. Fuel Type matches the FBO's fuel types (from the BusinessFuel table),
   * everything else matches the tag list. (Location/state/keyword narrowing already happened server-side.)
   */
  filterFbos(fbos: FboResult[]): FboResult[] {
    const f = this.query.fboFinderFilter;
    if (!f) {
      return fbos;
    }
    return fbos.filter(fbo => {
      const tags = fbo.tags || [];
      const fuelTypes = fbo.fuelTypes || [];

      // Amenities — AND logic (all selected tags).
      if (f.amenities.length && !f.amenities.every(a => tags.indexOf(a) !== -1)) {
        return false;
      }

      // Facilities — AND logic.
      if (f.facilities.length && !f.facilities.every(fa => tags.indexOf(fa) !== -1)) {
        return false;
      }

      // Line Services — AND logic.
      if (f.lineServices.length && !f.lineServices.every(l => tags.indexOf(l) !== -1)) {
        return false;
      }

      // Fuel — OR logic (any selected tag).
      if (f.fuel.length && !f.fuel.some(fu => tags.indexOf(fu) !== -1)) {
        return false;
      }

      // Fuel Type — OR logic, matched against the FBO's fuel types (not tags).
      if (f.fuelType.length && !f.fuelType.some(ft => fuelTypes.indexOf(ft) !== -1)) {
        return false;
      }

      // Rental Car — OR logic.
      if (f.rentalCar.length && !f.rentalCar.some(rc => tags.indexOf(rc) !== -1)) {
        return false;
      }

      // Transportation — OR logic.
      if (f.transportation.length && !f.transportation.some(t => tags.indexOf(t) !== -1)) {
        return false;
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (fbo.name || '').toLowerCase();
        const ident = (fbo.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchFbos() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.fbosState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.fbos = await this._fbosService.getFbosByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.fbos = await this._fbosService.getFbosByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.fbos = await this._fbosService.getFbosByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned FBOs so the
          // results (e.g. FBOs near a ZIP) are framed rather than leaving the map zoomed out to CONUS.
          this.fbos = await this._fbosService.getFbosBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.fboFinderFilter && this.query.fboFinderFilter.name) {
          // No location was entered, but an FBO name was: search by name (server-side name/ident match).
          this.fbos = await this._fbosService.getFbosBySearchTerm(this.query.fboFinderFilter.name);
          this._updateBounds();
        } else {
          this.fbos = [];
        }
        this.filteredFbos = this.filterFbos(this.fbos);
        this.fbosState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.fbosState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.fbosState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.fbos = [];
      this.filteredFbos = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.fbosState = State.Error;
    }
  }

  onServiceRepairFiltersChanged() {
    this.serializeQuery();
    this.filteredServiceRepairs = this.filterServiceRepairs(this.serviceRepairs);
  }

  /**
   * Applies the category facets client-side. So a user sees only the shops that can do everything they need,
   * each SERVICE group (Airframe / Avionics / Powerplant / Propeller / Interior / Exterior / Rotor) uses AND
   * within the group — a shop must offer every selected service — while each MANUFACTURER subsection uses OR
   * (a shop that services any of the selected makes qualifies, since you have one make). Groups are AND'd
   * together, and the keyword matches name or airport ident. (Location/state/keyword narrowing already
   * happened server-side.)
   */
  filterServiceRepairs(serviceRepairs: ServiceRepairResult[]): ServiceRepairResult[] {
    const f = this.query.serviceRepairFinderFilter;
    if (!f) {
      return serviceRepairs;
    }
    // Service facets: AND within the group (a shop must offer every selected service).
    const serviceGroups: string[][] = [
      f.airframe, f.avionics, f.powerplant, f.propeller, f.interior, f.exterior, f.rotor
    ];
    // Manufacturer facets: OR within the group (a shop that services any of the selected makes).
    const manufacturerGroups: string[][] = [
      f.airframeManufacturers, f.avionicsManufacturers, f.powerplantManufacturers, f.propellerManufacturers
    ];
    return serviceRepairs.filter(sr => {
      const tags = sr.tags || [];

      // AND within each service group, OR within each manufacturer group, AND across all groups.
      for (const group of serviceGroups) {
        if (group.length && !group.every(t => tags.indexOf(t) !== -1)) {
          return false;
        }
      }
      for (const group of manufacturerGroups) {
        if (group.length && !group.some(t => tags.indexOf(t) !== -1)) {
          return false;
        }
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (sr.name || '').toLowerCase();
        const ident = (sr.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchServiceRepairs() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.serviceRepairsState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.serviceRepairs = await this._serviceRepairsService.getServiceRepairsByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.serviceRepairs = await this._serviceRepairsService.getServiceRepairsByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.serviceRepairs = await this._serviceRepairsService.getServiceRepairsByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned businesses so the
          // results (e.g. shops near a ZIP) are framed rather than leaving the map zoomed out to CONUS.
          this.serviceRepairs = await this._serviceRepairsService.getServiceRepairsBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.serviceRepairFinderFilter && this.query.serviceRepairFinderFilter.name) {
          // No location was entered, but a business name was: search by name (server-side name/ident match).
          this.serviceRepairs = await this._serviceRepairsService.getServiceRepairsBySearchTerm(this.query.serviceRepairFinderFilter.name);
          this._updateBounds();
        } else {
          this.serviceRepairs = [];
        }
        this.filteredServiceRepairs = this.filterServiceRepairs(this.serviceRepairs);
        this.serviceRepairsState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.serviceRepairsState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.serviceRepairsState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.serviceRepairs = [];
      this.filteredServiceRepairs = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.serviceRepairsState = State.Error;
    }
  }

  onRentalSalesFiltersChanged() {
    this.serializeQuery();
    this.filteredRentalSales = this.filterRentalSales(this.rentalSales);
  }

  /**
   * Applies the category facets client-side. Aircraft Rental and Avionics Manufacturers use OR within the
   * group (a business offering any of the selected options qualifies) and are AND'd against the other
   * groups as usual. Sales Type and Aircraft Manufacturers get the same OR-within-group treatment, but with
   * one exception: businesses often only fill in one of those two facets, so a business missing just one of
   * them isn't excluded on the missing one — only a category it actually tagged must match the selected
   * filter. A business tagged in NEITHER category is still excluded when either facet has a selection (it's
   * not a plausible aircraft-for-sale match, e.g. a rental-only business). Avionics Manufacturers gets no
   * such leniency since avionics being sold can always be assumed new (there's no missing-category
   * ambiguity to account for). The keyword matches name or airport ident. (Location/state/keyword narrowing
   * already happened server-side.)
   */
  filterRentalSales(rentalSales: RentalSalesResult[]): RentalSalesResult[] {
    const f = this.query.rentalSalesFinderFilter;
    if (!f) {
      return rentalSales;
    }
    const SALES_TYPE_PREFIX = 'Sales::Types::';
    const AIRCRAFT_MANUFACTURER_PREFIX = 'Sales::Aircraft::';

    return rentalSales.filter(rs => {
      const tags = rs.tags || [];

      // Aircraft Rental — OR within the group, AND'd against the other groups.
      if (f.aircraftRental.length && !f.aircraftRental.some(t => tags.indexOf(t) !== -1)) {
        return false;
      }

      // Sales Type / Aircraft Manufacturers — businesses often only fill in one of the two, so a business
      // missing just one category isn't excluded on it. But a business tagged in NEITHER category isn't a
      // plausible match for a Sales Type/Manufacturer search at all (e.g. a rental-only business with no
      // aircraft for sale), so it's still excluded rather than passing both checks by default.
      const hasSalesTypeTags = tags.some(t => t.indexOf(SALES_TYPE_PREFIX) === 0);
      const hasAircraftManufacturerTags = tags.some(t => t.indexOf(AIRCRAFT_MANUFACTURER_PREFIX) === 0);
      if ((f.salesTypes.length || f.aircraftSalesManufacturers.length)
          && !hasSalesTypeTags && !hasAircraftManufacturerTags) {
        return false;
      }
      if (f.salesTypes.length && hasSalesTypeTags && !f.salesTypes.some(t => tags.indexOf(t) !== -1)) {
        return false;
      }
      if (f.aircraftSalesManufacturers.length && hasAircraftManufacturerTags
          && !f.aircraftSalesManufacturers.some(t => tags.indexOf(t) !== -1)) {
        return false;
      }

      // Avionics Manufacturers — no leniency; strict OR-within-group match as before.
      if (f.avionicsSalesManufacturers.length && !f.avionicsSalesManufacturers.some(t => tags.indexOf(t) !== -1)) {
        return false;
      }

      // Keyword — name or airport ident.
      if (f.name) {
        const term = f.name.toLowerCase();
        const name = (rs.name || '').toLowerCase();
        const ident = (rs.airportIdent || '').toLowerCase();
        if (name.indexOf(term) === -1 && ident.indexOf(term) === -1) {
          return false;
        }
      }

      return true;
    });
  }

  async searchRentalSales() {
    try {
      // A state search nulls this.query.state after use; on a later tab switch restore it from the preserved
      // value so validate() passes and this finder re-runs the same state search (see _finderStateProvince).
      if (!this.query.state && this._finderStateProvince) {
        this.query.state = this._finderStateProvince;
      }
      const validationResult = this.query.validate();
      if (validationResult.valid) {
        this.rentalSalesState = State.Loading;
        this.form.disableForm();
        this.serializeQuery();
        if (this.query.location && this.query.radius) {
          this.rentalSales = await this._rentalSalesService.getRentalSalesByLatLon(this.query.location, this.query.radius);
          if (!this.query.bounds) {
            this._updateBounds();
          }
        } else if (this.query.bounds) {
          this.rentalSales = await this._rentalSalesService.getRentalSalesByBoundingBox(this.query.bounds);
        } else if (this.query.state || this._finderStateProvince) {
          this.rentalSales = await this._rentalSalesService.getRentalSalesByState(this.query.state || this._finderStateProvince);
          this.query.state = null;
          this._updateBounds();
        } else if (this.query.term) {
          // Keyword and ZIP-code searches both arrive as a term; fit the map to the returned businesses so the
          // results (e.g. businesses near a ZIP) are framed rather than leaving the map zoomed out to CONUS.
          this.rentalSales = await this._rentalSalesService.getRentalSalesBySearchTerm(this.query.term);
          this._updateBounds();
        } else if (this.query.rentalSalesFinderFilter && this.query.rentalSalesFinderFilter.name) {
          // No location was entered, but a business name was: search by name (server-side name/ident match).
          this.rentalSales = await this._rentalSalesService.getRentalSalesBySearchTerm(this.query.rentalSalesFinderFilter.name);
          this._updateBounds();
        } else {
          this.rentalSales = [];
        }
        this.filteredRentalSales = this.filterRentalSales(this.rentalSales);
        this.rentalSalesState = State.Content;
        this.form.enableForm();
        this.errorMessage = null;
      } else {
        if (validationResult.reason === QueryValidationReason.MISSING_BASE_PARAMS) {
          this.rentalSalesState = State.Empty;
          return;
        }
        console.warn('Invalid search query validation result:', validationResult, 'Query:', this.query);
        this.errorMessage = validationResult.message;
        this.rentalSalesState = State.Error;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      this.rentalSales = [];
      this.filteredRentalSales = [];
      this.form.enableForm();
      this.errorMessage = null;
      this.rentalSalesState = State.Error;
    }
  }

  /**
   * Restores the selected airport after performing a search, causing the card to reopen. If the user clicks on an
   * airport, we center the map on the airport and open the card. However, re-centering the map triggers a new search,
   * which clears the markers and causes the card to close. We want to restore the card after the search completes.
   * @private
   */
  private _restoreSelectedAirport() {
    if (!this._selectedAirportId) {
      return;
    }
    setTimeout(() => {
      this.selectedAirport = this.filteredAirports.find(a => a.airportId === this._selectedAirportId) || null;
      // Clear the airport ID. This is mainly to prevent the card from reopening in case it was closed manually. We can't
      // differentiate between the card closing because the user deliberately closed it, vs it getting closed because a
      // new search was triggered after centering the map on the clicked airport.
      this._selectedAirportId = null;
    }, 300);
  }

  private _updateBounds() {
    this.pauseBounds = true;
    this.map.updateBounds();
    setTimeout(() => {
      this.pauseBounds = false;
    }, 1000);
  }

  onAirportCardHover(airport: AirportSearchResult, hover: boolean) {
    this.activeAirport = airport;
    this.map.toggleAirportInfoTooltip(airport, hover);
  }

  onActivityCardHover(activity: PlaceResult, hover: boolean) {
    if (hover) {
      this.selectedActivity = activity;
    }
  }

  onEventCardHover(aviationEvent: AviationEvent, hover: boolean) {
    this.activeAviationEvent = aviationEvent;
    this.map.toggleAviationEventInfoTooltip(aviationEvent, hover);
  }

  onAirportMarkerHover(airport: AirportSearchResult, hover: boolean) {
    this.activeAirport = hover ? airport : null;
    if (hover && this.selectedTab === this.airportsTab) {
      this.airportResultsListing.scrollToAirport(airport);
    }
  }

  onEventMarkerHover(aviationEvent: AviationEvent, hover: boolean) {
    this.activeAviationEvent = hover ? aviationEvent : null;
    if (hover && this.selectedTab === this.aviationEventsTab) {
      this.eventsResultsListing.scrollToEvent(aviationEvent);
    }
  }

  onFlyingClubCardHover(flyingClub: FlyingClubResult, hover: boolean) {
    this.activeFlyingClub = flyingClub;
    this.map.toggleFlyingClubInfoTooltip(flyingClub, hover);
  }

  onFlyingClubMarkerHover(flyingClub: FlyingClubResult, hover: boolean) {
    this.activeFlyingClub = hover ? flyingClub : null;
    if (hover && this.selectedTab === this.flyingClubsTab) {
      this.flyingClubFinderResultsListing.scrollToFlyingClub(flyingClub);
    }
  }

  onFlyingClubClick(flyingClub: FlyingClubResult) {
    // Record these search results as the "back" target before opening the club (mirrors the results list;
    // see FlyingclubPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('flyingClubSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/flyingclub', flyingClub.businessId]);
  }

  onFlightSchoolCardHover(flightSchool: FlightSchoolResult, hover: boolean) {
    this.activeFlightSchool = flightSchool;
    this.map.toggleFlightSchoolInfoTooltip(flightSchool, hover);
  }

  onFlightSchoolMarkerHover(flightSchool: FlightSchoolResult, hover: boolean) {
    this.activeFlightSchool = hover ? flightSchool : null;
    if (hover && this.selectedTab === this.flightSchoolsTab) {
      this.flightSchoolFinderResultsListing.scrollToFlightSchool(flightSchool);
    }
  }

  onFlightSchoolClick(flightSchool: FlightSchoolResult) {
    // Record these search results as the "back" target before opening the school (mirrors the results list;
    // see BusinessPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/business', flightSchool.businessId]);
  }

  onUniversityCardHover(university: UniversityResult, hover: boolean) {
    this.activeUniversity = university;
    this.map.toggleUniversityInfoTooltip(university, hover);
  }

  onUniversityMarkerHover(university: UniversityResult, hover: boolean) {
    this.activeUniversity = hover ? university : null;
    if (hover && this.selectedTab === this.universitiesTab) {
      this.universityFinderResultsListing.scrollToUniversity(university);
    }
  }

  onUniversityClick(university: UniversityResult) {
    // Record these search results as the "back" target before opening the university (mirrors the results
    // list; see BusinessPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/business', university.businessId]);
  }

  onFboCardHover(fbo: FboResult, hover: boolean) {
    this.activeFbo = fbo;
    this.map.toggleFboInfoTooltip(fbo, hover);
  }

  onFboMarkerHover(fbo: FboResult, hover: boolean) {
    this.activeFbo = hover ? fbo : null;
    if (hover && this.selectedTab === this.fbosTab) {
      this.fboFinderResultsListing.scrollToFbo(fbo);
    }
  }

  onFboClick(fbo: FboResult) {
    // Record these search results as the "back" target before opening the FBO (mirrors the results list;
    // see BusinessPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/business', fbo.businessId]);
  }

  onServiceRepairCardHover(serviceRepair: ServiceRepairResult, hover: boolean) {
    this.activeServiceRepair = serviceRepair;
    this.map.toggleServiceRepairInfoTooltip(serviceRepair, hover);
  }

  onServiceRepairMarkerHover(serviceRepair: ServiceRepairResult, hover: boolean) {
    this.activeServiceRepair = hover ? serviceRepair : null;
    if (hover && this.selectedTab === this.serviceRepairsTab) {
      this.serviceRepairFinderResultsListing.scrollToServiceRepair(serviceRepair);
    }
  }

  onServiceRepairClick(serviceRepair: ServiceRepairResult) {
    // Record these search results as the "back" target before opening the business (mirrors the results list;
    // see BusinessPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/business', serviceRepair.businessId]);
  }

  onRentalSalesCardHover(rentalSales: RentalSalesResult, hover: boolean) {
    this.activeRentalSales = rentalSales;
    this.map.toggleRentalSalesInfoTooltip(rentalSales, hover);
  }

  onRentalSalesMarkerHover(rentalSales: RentalSalesResult, hover: boolean) {
    this.activeRentalSales = hover ? rentalSales : null;
    if (hover && this.selectedTab === this.rentalSalesTab) {
      this.rentalSalesFinderResultsListing.scrollToRentalSales(rentalSales);
    }
  }

  onRentalSalesClick(rentalSales: RentalSalesResult) {
    // Record these search results as the "back" target before opening the business (mirrors the results list;
    // see BusinessPageComponent.goBack), and clear the airport back-target so search takes precedence.
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
    this._router.navigate(['/business', rentalSales.businessId]);
  }

  onAirportClick(airport: AirportSearchResult) {
    this.selectedAirport = airport;
    this._selectedAirportId = airport.airportId;
    this.map.toggleAirportInfoTooltip(airport, false);  // Close tooltip window
    this.map.setCenter(airport.location);
  }

  onCloseAirportCardWindow() {
    setTimeout(() => this.selectedAirport = null, 0);
  }

  onCloseActivityWindow(activity) {
    setTimeout(() => {
      if (this.selectedActivity === activity) {
        this.selectedActivity = null;
      }
    }, 0);
  }

  onActivityMarkerHover(activity: PlaceResult, hover: boolean) {
    if (hover) {
      this.selectedActivity = activity;
      if (this.selectedTab === this.activitiesTab) {
        this.activityResultsListing.scrollToActivity(activity);
      }
    }
  }

  onActivityClick(activity: PlaceResult) {
    // do nothing
  }

  toggleSidebar() {
    this.sidebarOpened = !this.sidebarOpened;
    // Initially hide the Back to Search button so it doesn't overlay the sidebar if it is being opened.
    // It may end up being shown again after a delay in onSidebarOpenedChange.
    this.backToSearchVisible = false;
  }

  onSidebarOpenedChange() {
    setTimeout(() => {
      this.map.updateSize();
      this.updateBackToSearch();
    }, 300);
  }

  serializeQuery() {
    this.query.type = this.searchType;
    this.query.aviationEventsFilter = this.aviationEventFilter;
    this.query.flyingClubFinderFilter = this.flyingClubFinderFilter;
    this.query.flightSchoolFinderFilter = this.flightSchoolFinderFilter;
    this.query.universityFinderFilter = this.universityFinderFilter;
    this.query.fboFinderFilter = this.fboFinderFilter;
    this.query.serviceRepairFinderFilter = this.serviceRepairFinderFilter;
    this.query.rentalSalesFinderFilter = this.rentalSalesFinderFilter;
    const params = this.query.encode();
    this._router.navigate([], { queryParams: params, replaceUrl: true });
  }

  deserializeQuery() {
    this.query = SearchQuery.decode(this._route.snapshot.queryParamMap);
    // Preserve the state search for finder tab-switching (see _finderStateProvince).
    this._finderStateProvince = this.query.state || null;
    if ((this.query.location || this.query.state) && this.query.locationDescription) {
      this.form.setQueryFieldValue(this.query.locationDescription);
    }
  }

  onFiltersChanged(keepPriceSliderState?: boolean) {
    this.serializeQuery();
    this.filteredAirports = this.query.execute(this.airports);

    if (this.query.bounds) {
      const declutterDistance = this.calculateDeclutterDistance(this.query.bounds.getAreaDegrees());
      if (declutterDistance > 0) {
        this.declutteredAirports = this.declutterMapMarkers(this.filteredAirports, declutterDistance);
      } else {
        this.declutteredAirports = this.filteredAirports;
      }
    } else {
      this.declutteredAirports = this.filteredAirports;
    }

    const anyFuelPriceFilterApplied = this.airportResultsListing.airportFilters.fuel100LLFilter.enabled
      || this.airportResultsListing.airportFilters.fuelJetAFilter.enabled
      || this.airportResultsListing.airportFilters.fuelMOGASFilter.enabled
      || this.airportResultsListing.airportFilters.fuelUL94Filter.enabled;

    // toggle on and off automatically when clicking fuel price filter
    if (keepPriceSliderState != null) {
      if (!keepPriceSliderState && anyFuelPriceFilterApplied) {
        this.airportResultsListing.showPrices = true;
      } else if (!keepPriceSliderState && !anyFuelPriceFilterApplied) {
        this.airportResultsListing.showPrices = false;
      }
    }

    if (anyFuelPriceFilterApplied && this.airportResultsListing.showPrices) {
      if (!this.declutteredAirports || this.declutteredAirports.length < 1) {
        return;
      }
      // divide min fuel prices for each airport into high, med, and low
      this.declutteredAirports.forEach(a => a.minPrice = this.getMinFuel(a));
      const lowMedIndexThreshold = Math.ceil(this.declutteredAirports.length / 3);
      const medHighIndexThreshold = this.declutteredAirports.length === 2 ? lowMedIndexThreshold : lowMedIndexThreshold * 2;

      this.declutteredAirports.sort((a, b) => a.minPrice - b.minPrice);
      this.declutteredAirports.forEach((a, i) => a.minPriceType = this.getMinPriceType(i, lowMedIndexThreshold, medHighIndexThreshold));
    } else {
      // reset minPrice and minPriceType to null
      this.declutteredAirports.forEach(a => {
        a.minPrice = null;
        a.minPriceType = null;
      });
    }
  }

  getMinFuel(airport: AirportSearchResult): number {
    if ((airport.fuel || []).length < 1) {
      return null;
    }

    const minFuel = airport.fuel
      .filter(f => {
        if (this.fuelType === FuelType.JetA && (f.fuelType === FuelType.JetA || f.fuelType === FuelType.JetAPremix)) {
          return true;
        }
        return f.fuelType === this.fuelType;
      })
      .sort((f1: AirportFuelSummary, f2: AirportFuelSummary) => f1.price - f2.price)[0];

    return minFuel ? minFuel.price : null;
  }

  getMinPriceType(index: number, lowMediumThreshold: number, mediumHighTreshold: number): string {
    if (index < 0 || lowMediumThreshold == null || mediumHighTreshold == null) {
      return null;
    }
    if (index >= mediumHighTreshold) {
      return 'High';
    } else if (index < mediumHighTreshold && index >= lowMediumThreshold) {
      return 'Medium';
    } else {
      return 'Low';
    }
  }

  calculateDeclutterDistance(areaDegrees: number): number {
    if (areaDegrees < 15) {
      return 0;
    } else if (areaDegrees >= 15 && areaDegrees < 50) {
      return 17;
    } else if (areaDegrees >= 50 && areaDegrees < 130) {
      return 22;
    } else {
      return 27;
    }

  }

  declutterMapMarkers(airportMarkers: AirportSearchResult[], declutterDistance: number): AirportSearchResult[] {
    if (airportMarkers.length < 1) {
      return airportMarkers;
    }

    let toReturn: AirportSearchResult[] = [];
    const markersTooClose: AirportSearchResult[] = [];

    let distance = 0;

    for (let i = 0; i < airportMarkers.length; i++) {
      for (let j = i + 1; j < airportMarkers.length; j++) {
        // if marker is already too close to another marker, skip it
        const marker = markersTooClose.find(a => a.airportId === airportMarkers[j].airportId);
        if (marker != null) {
          continue;
        }
        // for performance, assume if an airport is not in a neighboring state, it is far enough and we don't have to calc the distance
        if (!this.areStatesSameOrNeighbors(airportMarkers[i].stateProvince, airportMarkers[j].stateProvince)) {
          continue;
        }
        distance = airportMarkers[i].location.distanceTo(airportMarkers[j].location) * 0.000621371; // convert meters to miles

        if (distance < declutterDistance) {
          // if marker is too close add it to the list of too close markers
          markersTooClose.push(airportMarkers[j]);
        }
      }
    }

    // return the original array minus any markers that are too close
    toReturn = airportMarkers.filter(a => !markersTooClose.map(b => b.airportId).includes(a.airportId));

    return toReturn;
  }

  areStatesSameOrNeighbors(state1: string, state2: string): boolean {
    let toReturn = false;
    if (state1 == null || state2 == null) {
      return toReturn;
    }
    if (state1 === state2) {
      toReturn = true;
      return toReturn;
    }
    const state = this.NEIGHBORSTATES.find(s => s.code === state1);
    if (state != null) {
      const neighborStates = state.neighborCodes;
      toReturn = neighborStates.indexOf(state2) !== -1;
      return toReturn;
    }
  }

  onPublicUseChanged() {
    this.searchAirports();
  }

  onFuelPriceToggleChanged() {
    const anyFuelPriceFilterApplied = this.airportResultsListing.airportFilters.fuel100LLFilter.enabled
      || this.airportResultsListing.airportFilters.fuelJetAFilter.enabled
      || this.airportResultsListing.airportFilters.fuelMOGASFilter.enabled
      || this.airportResultsListing.airportFilters.fuelUL94Filter.enabled;

    if (!anyFuelPriceFilterApplied && this.airportResultsListing.showPrices) {
      this._snackBar.open('Please select a fuel type in the Airport Amenities list to display fuel prices', '', {
        duration: 6000
      });
    }
    this.onFiltersChanged();
  }

  onSelectedActivityChange() {
    this.serializeQuery();
    this.searchActivities();
  }

  onLocationStateChanged(state: State) {
    this.locationState = state;
    if (this.query.type !== SearchFormType.Activities) {
      this.activityState = State.Empty;
    }
    if (state !== State.Content) {
      this.activities = [];
    }
  }

  onShowAirportsToggleChanged() {
    // show or remove pins from display as appropriate
    if (this.eventsResultsListing.showAirports) {
      if (this.exceedsAirportBounds) {
        this.mapMessage = 'Zoom in to update airports displayed';
      }
      this.search(true);
    } else {
      // showAirports is false, so call the filter to apply it
      if (this.mapMessage === 'Zoom in to update airports displayed') {
        this.mapMessage = null;
      }
      this.onFiltersChanged();
    }
  }

  /**
   * Initializes the observable that determines when to issue a new search after the map bounds are updated.
   */
  private _initBoundsListener() {
    // Set up the boundsChange$ observable
    this.boundsChange$ = this.map.boundsChange.pipe(
      // Wait until bounds have stabilized before issuing a new search
      debounceTime(SEARCH_DEBOUNCE_TIME),

      // Avoid issuing a new search when WE programmatically fit the map (after a new search / recenter),
      // but still sync prevBounds to that fitted viewport. Otherwise prevBounds stays stale/null and the
      // first real user pan is either swallowed (used only to initialize prevBounds) or measured against a
      // stale box — so it takes an oversized pan before pan/zoom searches start updating.
      filter(b => {
        if (this.pauseBounds) {
          this.prevBounds = b;
          return false;
        }
        return true;
      }),

      // Avoid issuing a new search if this is the first update we received for the map bounds
      filter(b => {
        // Record whether we have a previous set of bounds
        const haveBounds = !!this.prevBounds;

        // If we don't have an initial set of bounds, store the current bounds so we have something to compare to
        // next time around.
        if (!haveBounds) {
          this.prevBounds = b;
        }

        // Return whether we had prevBounds
        return haveBounds;
      }),

      // Avoid issuing a search if the viewport bounding box area exceeds MAX_BBOX_AREA (or the wider
      // UNIVERSITY_MAX_BBOX_AREA when searching universities)
      filter(b => {
        const area = b.getAreaDegrees();
        const maxArea = this.query.type === SearchFormType.Universities ? UNIVERSITY_MAX_BBOX_AREA : MAX_BBOX_AREA;
        const exceedsBounds = area > maxArea;
        this.exceedsAirportBounds = exceedsBounds;
        if (this.query.type === SearchFormType.AviationEvents) {
          if (this.eventsResultsListing.showAirports) {
            this.mapMessage = exceedsBounds ? 'Zoom in to update airports displayed' : null;
            return true;
          } else {
            return true;
          }
        }
        this.mapMessage = exceedsBounds ? 'Zoom in to update results' : null;
        return !exceedsBounds;
      }),

      // Ensure the new bounding box is sufficiently different from the previous before issuing a search
      filter(b => !this.prevBounds || !b.equalTo(this.prevBounds, this.getBoundingBoxEpsilon(this.prevBounds))),

      // Store the new bounding box in prevBounds so we can compare it later
      tap(b => this.prevBounds = b)
    );

    // Subscribe to the boundsChange$ observable and issue a new search every time it emits an event.
    this.boundsChange$.subscribe(b => this.onBoundsChanged(b));
  }

  onBoundsChanged(bounds: BoundingBox) {
    // For a term search, ignore pan/zoom — EXCEPT for events, flying clubs, and flight schools, whose "term"
    // is a location (e.g. a ZIP or city resolved server-side to nearby results, not a name match), so panning
    // should re-query by the new bounds. (An airport term search is a name/ident match and must stay put.)
    if (this.query.term
        && this.query.type !== SearchFormType.AviationEvents
        && this.query.type !== SearchFormType.FlyingClubs
        && this.query.type !== SearchFormType.FlightSchools
        && this.query.type !== SearchFormType.Universities
        && this.query.type !== SearchFormType.Fbos
        && this.query.type !== SearchFormType.ServiceRepair
        && this.query.type !== SearchFormType.RentalSales) {
      return;
    }

    // Update the search query parameters with the new bounding box.
    this.query.location = null;
    this.query.radius = null;
    this.query.bounds = bounds;
    this.serializeQuery();

    // If we're only searching airports, clear out any old activity results.
    if (this.query.type !== SearchFormType.Activities) {
      this.activities = [];
      this.activityState = State.Empty;
    }
    /* if (this.query.type !== SearchFormType.AviationEvents) {
      this.aviationEvents = [];
      this.aviationEventsState = State.Empty;
    } */

    // Perform a new search
    this.search(true);
  }

  onPageChanged (e: PageEvent) {
    this.pageIndex = e.pageIndex;
  }

  onEventPageChanged (e: PageEvent) {
    this.eventPageIndex = e.pageIndex;
  }

  onFlyingClubPageChanged (e: PageEvent) {
    this.flyingClubPageIndex = e.pageIndex;
  }

  onFlightSchoolPageChanged (e: PageEvent) {
    this.flightSchoolPageIndex = e.pageIndex;
  }

  onUniversityPageChanged (e: PageEvent) {
    this.universityPageIndex = e.pageIndex;
  }

  onFboPageChanged (e: PageEvent) {
    this.fboPageIndex = e.pageIndex;
  }

  onServiceRepairPageChanged (e: PageEvent) {
    this.serviceRepairPageIndex = e.pageIndex;
  }

  onRentalSalesPageChanged (e: PageEvent) {
    this.rentalSalesPageIndex = e.pageIndex;
  }

  updateBackToSearch() {
    this.backToSearchVisible = !this.$sidebar['visible'](true);
    this._cdref.detectChanges();
    if (this.backToSearchVisible) {
      this.backToSearchEl.nativeElement.style.top = (window.pageYOffset + 4) + 'px';
      this.backToSearchEl.nativeElement.style.left = (window.pageXOffset + 24) + 'px';
    }
  }

  backToSearch() {
    this.backToSearchVisible = false;
    window.scroll(0, 0);
    this.sidebarOpened = true;
  }

  onClickNewSearch (event: MouseEvent) {
    event.preventDefault();
    if (this.selectedTab === this.aviationEventsTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'events'}});
    } else if (this.selectedTab === this.flyingClubsTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'flyingclubs'}});
    } else if (this.selectedTab === this.flightSchoolsTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'flightschools'}});
    } else if (this.selectedTab === this.universitiesTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'universities'}});
    } else if (this.selectedTab === this.fbosTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'fbos'}});
    } else if (this.selectedTab === this.serviceRepairsTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'servicerepair'}});
    } else if (this.selectedTab === this.rentalSalesTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'rentalsales'}});
    } else if (this.selectedTab === this.activitiesTab) {
      this._router.navigate(['/'], {queryParams: {'t': 'activities'}});
    } else {
      if (this.query && this.query.type && this.query.type === SearchFormType.FuelPrice) {
        this._router.navigate(['/'], {queryParams: {'t': 'fuel'}});
      } else {
        this._router.navigate(['/'], {queryParams: {'t': 'airports'}});
      }
    }
  }

  onError(message) {
    this.errorMessage = message;
    if (this.selectedTab === this.airportsTab) {
      this.airportState = State.Error;
    } else {
      this.activityState = State.Error;
    }
  }
}
